import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import whatsappServices from "./whatsappServices";

// Avisos push del inbox en el teléfono (Web Push).
//
// El permiso se pide desde un botón y no al montar: iOS solo muestra el diálogo si viene de
// un toque, y en Safari el Web Push existe únicamente con la página agregada a la pantalla
// de inicio y abierta desde ese ícono. Por eso cada estado dice qué hacer.
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;

function initialState() {
    const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    if (!supported) return isIOS() && !isStandalone() ? "ios-install" : "unsupported";
    if (Notification.permission === "denied") return "denied";
    if (Notification.permission === "granted") return "subscribing";
    return "idle";
}

export default function PushNotificationsControl() {
    const [state, setState] = useState(initialState);
    const [message, setMessage] = useState("");

    const subscribe = async () => {
        setState("subscribing");
        setMessage("");
        try {
            await whatsappServices.subscribePush();
            setState("active");
        } catch (e) {
            setState("error");
            setMessage(e?.response?.data?.errors?.join(", ") || e?.message || "No se pudo activar");
        }
    };

    // Con el permiso ya concedido se vuelve a registrar en cada visita: el servidor puede haber
    // borrado la suscripción (vencida) aunque el navegador la siga teniendo.
    useEffect(() => {
        if (state === "subscribing") subscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activate = async () => {
        const permission = await Notification.requestPermission();
        if (permission === "granted") return subscribe();
        setState(permission === "denied" ? "denied" : "idle");
    };

    const test = async () => {
        setMessage("Enviando…");
        try {
            const { sent, total } = await whatsappServices.testPush();
            setMessage(sent > 0 ? `Aviso enviado a ${sent} de ${total} dispositivo(s)` : "No se pudo enviar el aviso");
        } catch (e) {
            setMessage("No se pudo enviar el aviso");
        }
    };

    const hint = (text) => (
        <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 0.5 }}>{text}</Typography>
    );

    if (state === "ios-install") {
        return hint("Para recibir avisos en el iPhone: Compartir → Agregar a inicio, y abre Haulers desde ese ícono.");
    }
    if (state === "unsupported") return hint("Este navegador no permite notificaciones.");
    if (state === "denied") {
        return hint("Las notificaciones están bloqueadas para este sitio. Actívalas en los ajustes del navegador o del teléfono.");
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
            {state === "active" ? (
                <>
                    <Tooltip title="Este dispositivo recibe un aviso por cada mensaje entrante">
                        <Chip
                            icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />}
                            label="Notificaciones activas"
                            size="small"
                            sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontSize: 11, height: 22 }}
                        />
                    </Tooltip>
                    <Button size="small" onClick={test} sx={{ fontSize: 11, textTransform: "none" }}>
                        Probar
                    </Button>
                </>
            ) : (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={state === "subscribing"}
                    startIcon={<NotificationsOffOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={state === "error" ? subscribe : activate}
                    sx={{ fontSize: 11, textTransform: "none" }}
                >
                    {state === "subscribing" ? "Activando…" : state === "error" ? "Reintentar" : "Activar notificaciones"}
                </Button>
            )}
            {message && <Typography variant="caption" sx={{ color: "#888" }}>{message}</Typography>}
        </Box>
    );
}
